import type {
    CompensationAudience,
    CompensationBudgetInputMode,
    CompensationDistributionMode,
    CompensationDraftLine,
    CompensationEnvelope,
} from '@/types/compensation';

/** The draft's own editable fields, kept apart from `baseline` so `reset` restores them without a
 * refetch. */
export interface CompensationDraftFields {
    name: string;
    audience: CompensationAudience;
    budgetAmount: number;
    budgetInputMode: CompensationBudgetInputMode;
    distributionMode: CompensationDistributionMode;
    scoreThreshold: number;
    scoreSpread: number;
    levelMultipliers: Record<string, number>;
    lines: CompensationDraftLine[];
}

/** `baseline` is the last saved shape of the fields, so `isDirty` compares against state the
 * reducer already holds and `reset` needs no argument. */
export interface CompensationDraftState extends CompensationDraftFields {
    baseline: CompensationDraftFields;
}

export type CompensationDraftAction =
    | { type: 'setName'; name: string }
    | { type: 'setAudience'; audience: CompensationAudience }
    | { type: 'setBudget'; amount: number }
    | { type: 'setBudgetInputMode'; mode: CompensationBudgetInputMode }
    | { type: 'setDistributionMode'; mode: CompensationDistributionMode }
    | { type: 'setScoreThreshold'; value: number }
    | { type: 'setScoreSpread'; value: number }
    | { type: 'setLevelMultiplier'; levelId: string; value: number }
    | { type: 'setLineSalary'; employeeId: string; salary: number | null }
    | { type: 'setLinePercent'; employeeId: string; percent: number | null }
    | { type: 'lockLine'; employeeId: string; amount: number }
    | { type: 'unlockLine'; employeeId: string }
    | { type: 'setLineNote'; employeeId: string; note: string }
    | { type: 'applySalaries'; salaries: { employeeId: string; salary: number }[] }
    | { type: 'reset' };

/** Builds the draft fields from a saved envelope: settings as-is, and one draft line per computed
 * line carrying only the user's own inputs, never a computed value. */
const fieldsFromEnvelope = (envelope: CompensationEnvelope): CompensationDraftFields => ({
    name: envelope.name,
    audience: envelope.audience,
    budgetAmount: envelope.budgetAmount,
    budgetInputMode: envelope.budgetInputMode,
    distributionMode: envelope.distributionMode,
    scoreThreshold: envelope.scoreThreshold,
    scoreSpread: envelope.scoreSpread,
    levelMultipliers: envelope.levelMultipliers,
    lines: envelope.computed.lines.map((line) => ({
        employeeId: line.employeeId,
        salary: line.salary,
        salarySource: line.salarySource,
        chosenPercent: line.chosenPercent,
        isLocked: line.isLocked,
        lockedAmount: line.lockedAmount,
        note: line.note,
    })),
});

/** The draft of a freshly loaded envelope: nothing edited yet, so the fields are their own baseline. */
export const createDraft = (envelope: CompensationEnvelope): CompensationDraftState => {
    const fields = fieldsFromEnvelope(envelope);
    return { ...fields, baseline: fields };
};

/** Applies `applyToLine` to the draft line of `employeeId`, leaving the rest of the state untouched. */
const updateLine = (
    state: CompensationDraftState,
    employeeId: string,
    applyToLine: (line: CompensationDraftLine) => CompensationDraftLine,
): CompensationDraftState => ({
    ...state,
    lines: state.lines.map((line) => (line.employeeId === employeeId ? applyToLine(line) : line)),
});

export const draftReducer = (
    state: CompensationDraftState,
    action: CompensationDraftAction,
): CompensationDraftState => {
    switch (action.type) {
        case 'reset':
            return { ...state.baseline, baseline: state.baseline };

        case 'setName':
            return { ...state, name: action.name };

        case 'setAudience':
            return { ...state, audience: action.audience };

        case 'setBudget':
            return { ...state, budgetAmount: action.amount };

        case 'setBudgetInputMode':
            return { ...state, budgetInputMode: action.mode };

        case 'setDistributionMode':
            return { ...state, distributionMode: action.mode };

        case 'setScoreThreshold':
            return { ...state, scoreThreshold: action.value };

        case 'setScoreSpread':
            return { ...state, scoreSpread: action.value };

        case 'setLevelMultiplier':
            return {
                ...state,
                levelMultipliers: { ...state.levelMultipliers, [action.levelId]: action.value },
            };

        case 'setLineSalary':
            return updateLine(state, action.employeeId, (line) => ({
                ...line,
                salary: action.salary,
                salarySource: action.salary === null ? null : 'Entered',
            }));

        case 'setLinePercent':
            return updateLine(state, action.employeeId, (line) =>
                action.percent === null
                    ? { ...line, chosenPercent: null, isLocked: false }
                    : { ...line, chosenPercent: action.percent, isLocked: true },
            );

        case 'lockLine':
            return updateLine(state, action.employeeId, (line) => ({
                ...line,
                isLocked: true,
                lockedAmount: action.amount,
            }));

        case 'unlockLine':
            return updateLine(state, action.employeeId, (line) => ({
                ...line,
                isLocked: false,
                chosenPercent: null,
                lockedAmount: null,
            }));

        case 'setLineNote':
            return updateLine(state, action.employeeId, (line) => ({ ...line, note: action.note }));

        case 'applySalaries': {
            const salaryByEmployeeId = new Map(
                action.salaries.map(({ employeeId, salary }) => [employeeId, salary]),
            );
            return {
                ...state,
                lines: state.lines.map((line) => {
                    const salary = salaryByEmployeeId.get(line.employeeId);
                    return salary === undefined
                        ? line
                        : { ...line, salary, salarySource: 'Imported' as const };
                }),
            };
        }
    }
};
